import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import { i18nInstance, getLocalizationMessages } from '@/plugins/i18n';
import { router } from './router/router';
import gql from 'graphql-tag';
import graphqlClient from './helpers/graphqlClient';
import AvatarsService from '@/services/AvatarsService';
import LifeStageService from '@/services/LifeStageService';
import ProfileService from '@/services/ProfileService';
import CryptoJS from 'crypto-js';
import {
	setChooseSimulation,
	setLocalStorageData
} from './helpers/storageFunctions';
import ExpenseCategoryService from './services/ExpenseCategoryService';

import {
	debtNames,
	debtTypes,
	defaultNames,
	lockGameStages,
	applicationLoanTypes,
	lifeStageOrders,
	loanApplicableCategories,
	loansDefaultNames
} from '@/helpers/constants';

Vue.use(Vuex);
const getDefaultState = () => {
	return {
		showLogoutModal: false,
		showOnboarding: false,
		constants: null,
		userData: null,
		profileData: null,
		lifeStageData: null,
		expenseCategoryGroups: [],
		loading: false,
		errorMessages: [],
		errorMessagesGuids: [],
		stageSetupData: null,
		avatarImg: sessionStorage.getItem('avatar'),
		billboards: sessionStorage.getItem('billboards')
			? JSON.parse(sessionStorage.getItem('billboards'))
			: null,
		localSponsors: sessionStorage.getItem('localSponsors')
			? JSON.parse(sessionStorage.getItem('localSponsors'))
			: null,
		sessionLost: localStorage.getItem('sessionLost')
			? JSON.parse(localStorage.getItem('sessionLost'))
			: false,
		showWelcomeBack: sessionStorage.getItem('welcomeBack')
			? JSON.parse(sessionStorage.getItem('welcomeBack'))
			: true,
		resources: null,
		simPauseState: null,
		forcedToNextLifeStageState: null,
		sessionWillExpireState: null,
		initialDebt: null,
		budgetedCategoriesValue: 0,
		autoLoan: null,
		autoLoanBankingAndFinance: null,
		mortgageLoan: null,
		isAutoLoanDealerShip: false,
		isAutoLoanFinance: false,
		isMortgageLoan: false,
		hasApiKey: false,
		showModalLifeStageSummary: null,
		showModalLifeStageIntro: null,
		showModalConfirmBudgetVisible: null,
		showModalCalculateNMI: null,
		showModalAfterLifeStageIntro: null,
		showModalPassCode: null,
		localizationIrl: JSON.parse(process.env.VUE_APP_JA_LOCALIZATION_IRL),
		localizationDisable: JSON.parse(
			process.env.VUE_APP_JA_IS_LOCALIZATION_LOCALE
		),
		syncEnable: JSON.parse(process.env.VUE_APP_JA_SYNC_ENABLE),
		selectedFacility: null,
		isReturnOption: null,
		isLoadingProfileData: false
	};
};
const initialState = getDefaultState();

export default new Vuex.Store({
	state: initialState,
	getters: {
		selectedFacility: state => state.selectedFacility,
		syncEnable: state => state.syncEnable,
		isIrl: state => state.localizationIrl,
		constants: state => {
			if (state.constants) {
				return state.constants;
			}
		},
		sessionLost: state => state.sessionLost,
		showWelcomeBack: state => state.showWelcomeBack,
		simulationType: state => {
			if (state.userData) {
				return state.userData.userActiveSimulationTypeName;
			}
		},
		programIDByDefault: state => {
			if (state.constants) {
				return state.constants.umsUpperVirtualSimulationTypeProgramId;
			}
		},
		nationalSponsor: state => simulationType => {
			if (state.constants) {
				switch (simulationType) {
					case 'AdvancedVirtual':
						return state.constants.advancedVirtualLogo;
					case 'EntryVirtual':
						return state.constants.entryVirtualLogo;
					case 'AdvancedOnsite':
						return state.constants.advancedOnSiteLogo;
					case 'EntryOnsite':
						return state.constants.entryOnSiteLogo;

					default:
						return '';
				}
			}
		},
		resources: state => {
			return state.resources;
		},
		currentDisplayOrder: state => {
			if (state.userData) {
				return state.userData.studentLifeStageDisplayOrder;
			}
		},
		userData: state => {
			return state.userData;
		},
		lifeStageData: state => {
			return state.lifeStageData;
		},
		isSimulationCompleted: state => {
			if (state.userData && !state.userData.studentNextLifeStageId) {
				return true;
			}
			return false;
		},
		profileData: state => {
			return state.profileData;
		},
		stageSetupData: state => {
			return state.stageSetupData;
		},
		avatarImg: state => {
			return state.avatarImg;
		},
		localSponsor: state => moduleName => {
			if (state.localSponsors) {
				return state.localSponsors[moduleName];
			} else {
				return null;
			}
		},
		isLoadingProfileData: state => {
			return state.isLoadingProfileData;
		},
		categoryImages: state => {
			return state.categoryImages;
		},
		billboards: state => {
			return state.billboards;
		},
		billboardByCategory: state => categoryID => {
			return state.billboards[categoryID];
		},
		showLogoutModal: state => {
			return state.showLogoutModal;
		},
		showOnboarding: state => {
			return state.showOnboarding;
		},
		simPauseState: state => {
			return state.simPauseState;
		},
		isReturnOption: state => {
			return state.isReturnOption;
		},
		sessionWillExpireState: state => {
			return state.sessionWillExpireState;
		},
		forcedToNextLifeStageState: state => {
			return state.forcedToNextLifeStageState;
		},
		expenseCategoryGroups: state => {
			return state.expenseCategoryGroups;
		},
		expenseCategories: state => {
			let categories = null;

			if (state.expenseCategoryGroups.length) {
				categories = [];
				state.expenseCategoryGroups.forEach(categoryGroup => {
					categories = categories.concat(categoryGroup.cmsExpenseCategories);
				});

				// Add "isBudgeted" Property based on the data in each object
				categories = categories.map(category => {
					if (category.defaultName === loansDefaultNames.AUTO_LOAN_DEALERSHIP) {
						state.isAutoLoanDealerShip = true;
					}
					if (
						category.defaultName ===
						loansDefaultNames.AUTO_LOAN_BANKING_AND_FINANCE
					) {
						state.isAutoLoanFinance = true;
					}
					if (category.defaultName === loansDefaultNames.MORTGAGE_LOAN) {
						state.isMortgageLoan = true;
					}
					return {
						...category,
						// isBudgeted  means no transferred values from
						// previous life stage
						isBudgeted: !!category.studentExpenseCategoryBudgets.some(
							item =>
								!item.isAutomaticallyCreated && !item.isLockedForCurrentStudent
						),
						allocatedBudget: category.studentExpenseCategoryBudgets[0]
							? category.studentExpenseCategoryBudgets[0].budget
							: 0,
						totalBudgetedCategories: state.budgetedCategoriesValue
					};
				});

				categories = categories.filter(
					category =>
						!category.defaultName
							.toLowerCase()
							.includes(defaultNames.AUTO_LOAN.toLowerCase()) &&
						!category.defaultName
							.toLowerCase()
							.includes(defaultNames.MORTGAGE_LOAN.toLowerCase())
				);
			}

			return categories;
		},
		budgetedCategoriesValue: state => {
			return state.budgetedCategoriesValue;
		},
		areAllBudgetsSet: (state, getters) => {
			let categoriesCheck = false;

			if (getters.expenseCategories) {
				categoriesCheck = getters.expenseCategories
					.filter(item => !item.isLockedForCurrentStudent)
					.every(category => {
						const categoryBudget =
							category.studentExpenseCategoryBudgets[0] || null;
						return (
							categoryBudget &&
							categoryBudget.budget &&
							categoryBudget.budget !== 0
						);
					});
			}
			return categoriesCheck;
		},
		isInBudgetingPhase: (state, getters) => {
			let categoriesCheck = false;
			categoriesCheck = !getters.areAllBudgetsSet;

			if (!state.userData) {
				return false;
			}

			return categoriesCheck || !state.userData.hideConfirmBudgetPopup;
		},
		hasApiKey: state => {
			return state.hasApiKey;
		},
		errorMessages: state => {
			return state.errorMessages;
		},
		errorMessagesGuids: state => {
			return state.errorMessagesGuids;
		},
		initialDebts: state => {
			return state.initialDebt;
		},
		debts: state => {
			let debts = null;

			if (state.profileData && state.lifeStageData) {
				const creditCardBalance =
					state.profileData.cmsLifeStage.creditCardLimit -
					state.lifeStageData.creditCardDebt;

				const creditCardPayment =
					state.lifeStageData.minimumStudentCreditCardPayment;

				debts = {};

				debts.creditCard = {
					name: debtNames.CREDIT_CARD,
					amount: creditCardPayment >= 0 ? creditCardPayment : 0,
					balance: creditCardBalance >= 0 ? creditCardBalance : 0,
					creditLimit: state.profileData.cmsLifeStage.creditCardLimit,
					type: debtTypes.CREDIT_CARD
				};

				debts.educationLoan = {
					name: debtNames.EDUCATION,
					amount: state.profileData.minimumStudentLoanPayment,
					remainingBalance: state.profileData.studentLoanDebt,
					totalBalance: state.profileData.totalStudentLoan,
					type: debtTypes.EDUCATION
				};

				debts.autoLoan = {
					name: debtNames.AUTO_LOAN,
					totalBalance: 0,
					remainingBalance: 0,
					amount: 0,
					interest: 0,
					principal: 0,
					additionalCost: 0,
					type: debtTypes.AUTO_LOAN
				};

				debts.mortgageLoan = {
					name: debtNames.MORTGAGE_LOAN,
					totalBalance: 0,
					remainingBalance: 0,
					amount: 0,
					interest: 0,
					principal: 0,
					additionalCost: 0,
					type: debtTypes.MORTGAGE_LOAN
				};

				debts.autoLoanDealership = {
					name: debtNames.AUTO_LOAN_DEALERSHIP,
					totalBalance: 0,
					remainingBalance: 0,
					amount: 0,
					interest: 0,
					principal: 0,
					additionalCost: 0,
					type: debtTypes.AUTO_LOAN_DEALERSHIP
				};

				if (state.initialDebt) {
					Object.values(debts).forEach(debt => {
						const initialDebtObject = state.initialDebt[debt.type];
						if (initialDebtObject) {
							debt.id = initialDebtObject.id;
							debt.budget = initialDebtObject.budget;
							if (
								[
									debtTypes.MORTGAGE_LOAN,
									debtTypes.AUTO_LOAN_DEALERSHIP,
									debtTypes.AUTO_LOAN
								].includes(debt.type)
							) {
								debt.remainingBalance =
									debt.type == debtTypes.MORTGAGE_LOAN
										? state.profileData.mortgageLoanOutstandingAmount
										: state.profileData.autoLoanOutstandingAmount;
								debt.totalBalance = initialDebtObject.cost;
								debt.amount = initialDebtObject.amount;
								debt.additionalCost = initialDebtObject.additionalCost;
								debt.interest =
									debt.type == debtTypes.MORTGAGE_LOAN
										? state.profileData.mortgageLoanInterestAmount
										: state.profileData.autoLoanInterestAmount;
								debt.principal =
									debt.amount - debt.additionalCost - debt.interest;
							}
						}
					});
				}
			}

			return debts;
		},
		debtsTotalAmount: (state, getters) => {
			if (!getters.debts) {
				return 0;
			}

			return Object.values(getters.debts).reduce((sum, item) => {
				if (item.budget > 0) {
					return sum + parseInt(item.budget);
				} else {
					return sum;
				}
			}, 0);
		},
		savingsTotalAmount: (state, getters) => {
			return getters.savings.reduce((sum, item) => {
				if (item.amount > 0) {
					return sum + parseInt(item.amount);
				} else {
					return sum;
				}
			}, 0);
		},
		savings: state => {
			let savings = [];

			if (state.lifeStageData) {
				savings = [];
				for (
					let i = 0, len = state.lifeStageData.studentGoals.length;
					i < len;
					i++
				) {
					let currentGoal = state.lifeStageData.studentGoals[i];
					let goalAmount =
						currentGoal.studentGoalSavings.length &&
						currentGoal.studentGoalSavings[0].amount
							? parseInt(currentGoal.studentGoalSavings[0].amount)
							: 0;
					let savingObj = {
						id: currentGoal.studentGoalSavings[0].id,
						amount: goalAmount,
						studentGoalId: state.lifeStageData.studentGoals[i].id,
						lifeStage_ID: state.lifeStageData.lifeStage_ID,
						accountBalance: currentGoal.totalSavingsAmount
					};
					if (currentGoal.cmsGoal) {
						savingObj.description = currentGoal.cmsGoal.description;
						savingObj.name = currentGoal.cmsGoal.name;
						savingObj.totalAmount = currentGoal.studentGoalSavings.length
							? currentGoal.studentGoalSavings[0].totalAmount
							: null;
					} else {
						savingObj.name = 'generalSavings';
					}
					savings.push(savingObj);
				}
			}
			return savings;
		},
		isSimulationType: state => type => {
			return (
				state.userData &&
				state.userData.userActiveSimulationTypeName
					.toLowerCase()
					.includes(type.toLowerCase())
			);
		},
		haveToUsePasscodes: (state, getters) => {
			return getters.isSimulationType('onsite');
		},
		areSavingsAllocated: (state, getters) => {
			return getters.savingsTotalAmount > 0;
		},
		getLockState: (state, getters) => {
			// TODO: Update this to enable other lock stages
			if (!getters.isSimulationType('onsite')) {
				return lockGameStages.SHOPPING;
			}
			if (state.showModalPassCode) {
				return lockGameStages.SHOPPING;
			}
			if (getters.isSimulationType('EntryOnsite')) {
				switch (true) {
					case state.profileData && state.showModalConfirmBudgetVisible:
						return lockGameStages.BUDGETING;
					case state.profileData && state.showModalAfterLifeStageIntro:
						return lockGameStages.AFTER_LIFE_STAGE_INTRO;
					case state.profileData && state.showModalCalculateNMI:
						return lockGameStages.AFTER_CALCULATE_NMI;
					case state.profileData &&
						state.showModalLifeStageIntro &&
						!state.showModalPassCode:
						return lockGameStages.LIFE_SCENARIO;
					case state.profileData && state.showModalPassCode:
						return lockGameStages.SHOPPING;
					default:
						return lockGameStages.SHOPPING;
				}
			}
			if (getters.isSimulationType('AdvancedOnsite')) {
				switch (true) {
					case state.profileData && state.showModalConfirmBudgetVisible:
						return lockGameStages.BUDGETING;
					case state.profileData && state.showModalAfterLifeStageIntro:
						return lockGameStages.AFTER_LIFE_STAGE_INTRO;
					case state.profileData && state.showModalCalculateNMI:
						return lockGameStages.AFTER_CALCULATE_NMI;
					case state.profileData &&
						state.showModalLifeStageIntro &&
						!state.showModalPassCode:
						return lockGameStages.LIFE_SCENARIO;
					case state.showModalLifeStageSummary:
						return lockGameStages.LIFE_STAGE_SUMMARY;
					case state.profileData && state.showModalPassCode:
						return lockGameStages.SHOPPING;
					default:
						return lockGameStages.SHOPPING;
				}
			}
		},
		isLockStage: (state, getters) => lockStage => {
			return getters.getLockState === lockStage;
		},
		autoLoan: state => {
			return state.autoLoan;
		},
		autoLoanBankingAndFinance: state => {
			return state.autoLoanBankingAndFinance;
		},
		mortgageLoan: state => {
			return state.mortgageLoan;
		},
		isAutoLoanAppliedBanking: state => {
			return (
				state.autoLoanBankingAndFinance &&
				state.autoLoanBankingAndFinance.hasApplied
			);
		},
		isAutoLoanApplied: (state, getters) => {
			if (
				getters.isAutoLoanDealershipVisible &&
				!getters.isAutoLoanFinanceVisible
			) {
				return state.autoLoan && state.autoLoan.hasApplied;
			}
			// Verificar si solo hay visibilidad en el préstamo de financiamiento
			else if (
				!getters.isAutoLoanDealershipVisible &&
				getters.isAutoLoanFinanceVisible
			) {
				return (
					state.autoLoanBankingAndFinance &&
					state.autoLoanBankingAndFinance.hasApplied
				);
			}
			// Verificar si ambos tipos de préstamos están visibles
			else if (
				getters.isAutoLoanDealershipVisible &&
				getters.isAutoLoanFinanceVisible
			) {
				return (
					(state.autoLoan && state.autoLoan.hasApplied) ||
					(state.autoLoanBankingAndFinance &&
						state.autoLoanBankingAndFinance.hasApplied)
				);
			} else {
				return true;
			}
		},
		isAutoLoanAppliedDealerShip: state => {
			return state.autoLoan && state.autoLoan.hasApplied;
		},
		isMortgageLoanApplied: (state, getters) => {
			if (!getters.isMortgageLoanVisible) {
				return true;
			}
			return state.mortgageLoan && state.mortgageLoan.hasApplied;
		},
		isLoanVisible: state => {
			return (
				state.profileData &&
				state.profileData.cmsLifeStage.displayOrder !== lifeStageOrders.TEENAGER
			);
		},
		isAutoLoanDealershipVisible: (state, getters) => {
			return (
				state.isAutoLoanDealerShip &&
				getters.isLoanVisible &&
				getters.isLoanApplicableCategoryVisible(
					loanApplicableCategories.TRANSPORTATION
				) &&
				getters.autoLoan.expenseCategory_ID !== ''
			);
		},
		isAutoLoanFinanceVisible: (state, getters) => {
			return (
				state.isAutoLoanFinance &&
				getters.isLoanVisible &&
				getters.isLoanApplicableCategoryVisible(
					loanApplicableCategories.TRANSPORTATION
				) &&
				getters.autoLoan.expenseCategory_ID !== ''
			);
		},
		isMortgageLoanVisible: (state, getters) => {
			return (
				state.isMortgageLoan &&
				getters.isLoanVisible &&
				getters.isLoanApplicableCategoryVisible(
					loanApplicableCategories.HOUSING
				) &&
				getters.mortgageLoan.expenseCategory_ID !== ''
			);
		},
		isLoanApplicableCategoryVisible: (state, getters) => category => {
			if (!getters.areLoansAvailableBySimulation) {
				return false;
			}

			return (
				getters.expenseCategories &&
				!!getters.expenseCategories.find(c => c.assetName === category)
			);
		},
		isLoanRequired: (state, getters) => {
			const requiredLifeStages = [
				lifeStageOrders.YOUNG_ADULT,
				lifeStageOrders.WORKING_ADULT,
				lifeStageOrders.ADULT_RETIREMENT
			];
			const isAutoLoanAvailable = getters.isLoanApplicableCategoryVisible(
				loanApplicableCategories.TRANSPORTATION
			);
			const isMortgageLoanAvailable = getters.isLoanApplicableCategoryVisible(
				loanApplicableCategories.HOUSING
			);

			return (
				(isAutoLoanAvailable || isMortgageLoanAvailable) &&
				state.profileData &&
				requiredLifeStages.includes(state.profileData.cmsLifeStage.displayOrder)
			);
		},
		areLoansAvailableBySimulation: (state, getters) => {
			// INFO: Remove this function and its usage or simply return true
			// To enable Loans in virtual Simulations (if needed)
			return getters.isSimulationType('onsite');
		}
	},
	mutations: {
		setSelectedFacility(state, selectedFacility) {
			state.selectedFacility = selectedFacility;
			const key = CryptoJS.SHA256(state.userData.email).toString();
			localStorage.setItem(key, JSON.stringify(selectedFacility));
		},
		setHasApiKey: (state, stage) => {
			state.hasApiKey = stage;
		},
		setShowModalLifeStageSummary: (state, stage) => {
			state.showModalLifeStageSummary = stage;
		},
		setShowModalLifeStageIntro: (state, stage) => {
			state.showModalLifeStageIntro = stage;
		},
		setShowModalConfirmBudgetVisible: (state, stage) => {
			state.showModalConfirmBudgetVisible = stage;
		},
		setShowModalPassCode: (state, stage) => {
			state.showModalPassCode = stage;
		},
		setShowModalAfterLifeStageIntro: (state, stage) => {
			state.showModalAfterLifeStageIntro = stage;
		},
		setShowModalCalculateNMI: (state, stage) => {
			state.showModalCalculateNMI = stage;
		},
		setShowWelcomeBack: (state, stage) => {
			state.showWelcomeBack = stage;
			if (!stage) {
				sessionStorage.setItem('welcomeBack', false);
			}
		},
		resetState(state) {
			const defaultState = getDefaultState();
			for (let f in state) {
				Vue.set(state, f, defaultState[f]);
			}
		},
		constants: (state, payload) => {
			state.constants = payload;
		},
		logout: (state, payload) => {
			//Prevents duplicate logout redirection in a period of less than a second.
			const now = Date.now();
			const lastLogout = parseInt(sessionStorage.getItem('lastLogout'));
			let redirect = false;
			if (isNaN(lastLogout)) {
				redirect = true;
			} else {
				if (now - lastLogout > 1000) {
					redirect = true;
				}
			}
			if (redirect === true) {
				state.userData = null;
				sessionStorage.clear();
				sessionStorage.setItem('lastLogout', now.toString());
				// TODO: Update this to use constants
				localStorage.removeItem('ceUTC');
				window.location.href = payload.logoutURL;
			}
		},
		setSessionLost: (state, payload) => {
			if (payload) {
				state.sessionLost = true;
				localStorage.setItem('sessionLost', true);
			} else {
				state.sessionLost = false;
				localStorage.removeItem('sessionLost');
			}
		},
		setUserData: (state, payload) => {
			state.userData = payload.data;
			state.userData.isViewAsStudentEnabled = false;
			if (sessionStorage.getItem('viewAsStudent')) {
				const viewAsStudent = JSON.parse(
					sessionStorage.getItem('viewAsStudent')
				);
				state.userData.isViewAsStudentEnabled = viewAsStudent
					? viewAsStudent.isViewAsStudent
					: false;
			}
			if (payload.firstLogin) {
				router.push({ name: payload.data.urlPathToNavigateAfterLogin });
			}
		},
		setLifeStageData: (state, payload) => {
			state.lifeStageData = payload;
		},
		setProfileData: (state, payload) => {
			state.profileData = payload;
		},
		setStageSetupData: (state, payload) => {
			state.stageSetupData = payload;
		},
		setExpenseCategoryGroups: (state, payload) => {
			state.expenseCategoryGroups = payload;
		},
		setResources: (state, payload) => {
			state.resources = payload;
		},
		setBudgetedCategoriesValue: (state, payload) => {
			state.budgetedCategoriesValue = payload;
		},
		resetLifeStageData: state => {
			state.lifeStageData = null;
		},
		resetProfileData: state => {
			state.profileData = null;
		},
		resetCategories: state => {
			state.expenseCategoryGroups = [];
		},

		setAvatarImg: (state, payload) => {
			sessionStorage.setItem('avatar', payload);
			state.avatarImg = payload;
		},

		setLocalSponsors: (state, payload) => {
			const localSponsors = {};
			payload.forEach(module => {
				localSponsors[module.name] = module.logoBase64;
			});
			sessionStorage.setItem('localSponsors', JSON.stringify(localSponsors));
			state.localSponsors = localSponsors;
		},

		clearLocalSponsors: state => {
			sessionStorage.removeItem('localSponsors');
			state.localSponsors = null;
		},

		setBillboards: (state, payload) => {
			const billboards = {};
			payload.forEach(categoryGroup => {
				categoryGroup.cmsExpenseCategories.forEach(category => {
					billboards[category.expenseCategory_ID] =
						category.billboardLogoBase64;
				});
			});
			sessionStorage.setItem('billboards', JSON.stringify(billboards));
			state.billboards = billboards;
		},

		clearBillboards: state => {
			sessionStorage.removeItem('billboards');
			state.billboards = null;
		},
		closeLogoutModal: state => {
			state.showLogoutModal = false;
		},
		openLogoutModal: state => {
			state.showLogoutModal = true;
		},
		toggleOnboarding: (state, payload) => {
			state.showOnboarding = payload;
		},
		setLoading: (state, payload) => {
			state.loading = payload;
		},
		setIsLoadingProfileData: (state, payload) => {
			state.isLoadingProfileData = payload;
		},
		setErrorMessages: (state, payload) => {
			if (payload.length) {
				state.errorMessages = payload.map(x => x.message);
			} else {
				state.errorMessages = [];
			}
		},
		setErrorMessagesGuids: (state, payload) => {
			if (payload.length) {
				state.errorMessagesGuids = payload.map(x => x.entityGuid);
			} else {
				state.errorMessages = [];
			}
		},
		setPauseState: (state, payload) => {
			state.simPauseState = payload;
		},
		setForcedToNextLifeStageState: (state, payload) => {
			state.forcedToNextLifeStageState = payload;
		},
		setFacilitySelected: (state, payload) => {
			state.facilitySelected = payload;
		},
		setIsReturnOption: (state, payload) => {
			state.isReturnOption = payload;
		},
		setSessionWillExpireState: (state, payload) => {
			state.sessionWillExpireState = payload;
		},
		setInitialDebt: (state, payload) => {
			state.initialDebt = payload;
		},
		setAutoLoan: (state, payload) => {
			state.autoLoan = payload;
		},
		setAutoLoanBanking: (state, payload) => {
			state.autoLoanBankingAndFinance = payload;
		},
		setMortgageLoan: (state, payload) => {
			state.mortgageLoan = payload;
		},
		updateUserData: (state, payload) => {
			for (let property in state.userData) {
				// update only what needs to be updated
				// a.k.a what's in the payload
				if (payload.hasOwnProperty(property)) {
					Vue.set(state.userData, property, payload[property]);
				}
			}
		}
	},
	actions: {
		updateSelectedFacility({ commit }, facility) {
			commit('setSelectedFacility', facility);
		},
		clearBillboards({ commit }) {
			commit('clearBillboards');
		},
		clearLocalSponsors({ commit }) {
			commit('clearLocalSponsors');
		},
		reset({ commit }) {
			commit('resetState');
		},
		hideModalLifeStageSummary({ commit }) {
			commit('setShowModalLifeStageSummary', true);
		},
		hideModalLifeStageIntro({ commit }) {
			commit('setShowModalLifeStageIntro', true);
		},
		hideModalConfirmBudgetVisible({ commit }) {
			commit('setShowModalConfirmBudgetVisible', true);
		},
		hideModalAfterLifeStageIntro({ commit }) {
			commit('setShowModalAfterLifeStageIntro', true);
		},
		hideModalCalculateNMI({ commit }) {
			commit('setShowModalCalculateNMI', true);
		},
		hideModalPassCode({ commit }) {
			commit('setShowModalPassCode', true);
		},
		showLifeStageSummary({ commit }) {
			commit('setShowModalLifeStageSummary', false);
		},
		showLifeStageIntro({ commit }) {
			commit('setShowModalLifeStageIntro', false);
		},
		showModalConfirmBudgetVisible({ commit }) {
			commit('setShowModalConfirmBudgetVisible', false);
		},
		showModalAfterLifeStageIntro({ commit }) {
			commit('setShowModalAfterLifeStageIntro', false);
		},
		showModalCalculateNMI({ commit }) {
			commit('setShowModalCalculateNMI', false);
		},
		showModalPassCode({ commit }) {
			commit('setShowModalPassCode', false);
		},
		getConstants: ({ commit }) => {
			return new Promise((resolve, reject) => {
				return axios
					.get('/api/Config/GetConfigurationConstants', {
						withCredentials: true
					})
					.then(resp => {
						if (resp.data) {
							if (resp.data.lastDeployTimeUtc) {
								let localStorageDeployTime = localStorage.getItem(
									'lastDeployTimeUtc'
								);

								if (localStorageDeployTime) {
									// exist => compare
									let storageDate = new Date(localStorageDeployTime);
									let respDate = new Date(resp.data.lastDeployTimeUtc);

									if (respDate > storageDate) {
										// hard reload the browser
										localStorage.setItem(
											'lastDeployTimeUtc',
											resp.data.lastDeployTimeUtc
										);

										window.location.reload(true);
									}
								} else {
									localStorage.setItem(
										'lastDeployTimeUtc',
										resp.data.lastDeployTimeUtc
									);
								}
							}

							commit('constants', resp.data);
						}
						resolve(resp);
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		isUserAuthenticated: () => {
			return new Promise((resolve, reject) => {
				axios
					.get('/api/Account/IsUserAuthenticated', { withCredentials: true })
					.then(resp => {
						if (resp.data) {
							resolve(true);
						}
						resolve(false);
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		keepSessionAlive: () => {
			return new Promise((resolve, reject) => {
				axios
					.get('/api/Account/RefreshSession', { withCredentials: true })
					.then(() => {
						resolve(true);
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		auth: ({ dispatch }) => {
			dispatch('isUserAuthenticated').then(response => {
				if (response) {
					dispatch('getUserData', true);
				} else {
					window.location.href =
						process.env.VUE_APP_API_URL + '/api/Account/LogInExternal';
				}
			});
		},
		async getHasApiKey({ commit }) {
			return new Promise((resolve, reject) => {
				axios
					.get('/api/Data/HasApiKey', {
						withCredentials: true,
						headers: {
							'Cache-Control': 'no-cache'
						}
					})
					.then(resp => {
						if (resp.data) {
							commit('setHasApiKey', resp.data);
						}
						resolve(resp);
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		async updateLocalization({ state }, facilityId) {
			if (!state.localizationDisable && facilityId != null) {
				let messages = await getLocalizationMessages(facilityId);
				for (const [key, value] of Object.entries(messages)) {
					i18nInstance.setLocaleMessage(key, value);
				}
			}
		},
		async getUserData({ commit, dispatch }, isLogin) {
			return new Promise((resolve, reject) => {
				axios
					.get('/api/Account/GetAuthenticatedUserData', {
						withCredentials: true,
						headers: {
							'Cache-Control': 'no-cache'
						},
						params: {
							t: Date.now()
						}
					})
					.then(resp => {
						if (resp.data) {
							sessionStorage.setItem(
								'studentFacilityId',
								resp.data.studentFacilityId
							);

							dispatch('updateLocalization', resp.data.studentFacilityId);

							if (
								resp.data.userActiveSimulationId &&
								resp.data.studentId &&
								!resp.data.manyActiveSimulations
							) {
								setChooseSimulation({
									id: parseInt(resp.data.userActiveSimulationId),
									sid: resp.data.studentId
								});
							}

							setLocalStorageData('ceUTC', resp.data.cookieExpiresUtc);

							commit('setUserData', {
								data: resp.data,
								firstLogin: isLogin
							});
						}
						resolve(resp);
					})
					.catch(err => {
						reject(err);
					});
			});
		},

		async getLocalSponsors({ commit, state }) {
			const simulationSponsorModules = [
				//'PrimaryBillboard',
				'Profile',
				'LifeStageSummary',
				'SecondaryBillboard'
			];
			const moduleNameFilter = [];

			simulationSponsorModules.forEach(module =>
				moduleNameFilter.push(`LocalLogoModule.name=\\"${module}\\"`)
			);

			const response = await graphqlClient.query({
				query: gql`
					{
						cmsSponsorLogosLocalLogoModules(where: "SponsorLogo.isActive=true && (${moduleNameFilter.join(
							'||'
						)}) &&  (SponsorLogo.isLocalSponsor = true || SponsorLogo.isNationalSponsor = true) && SponsorLogo.facility_ID=${
					state.userData.studentFacilityId
				} && SponsorLogo.simulationType_ID=${
					state.userData.userActiveSimulationTypeId
				}") {
							localLogoModule {
								name
							}
							sponsorLogo {
								logoBase64
							}
						},
            cmsSponsorLogos(
							where: "isNationalSponsor = true && isActive = true && facility_ID = 0 && simulationType_ID=${
								state.userData.userActiveSimulationTypeId
							}"
						) {
							logoBase64
						}
					}
				`,

				fetchPolicy: 'no-cache'
			});

			if (response.data && response.data.cmsSponsorLogosLocalLogoModules) {
				// Merge the two arrays
				let sponsors = [
					...response.data.cmsSponsorLogosLocalLogoModules,
					...response.data.cmsSponsorLogos
				];

				// remap the data in order to be unified because
				// cmsSponsorLogos returns a different objects' properties
				sponsors = sponsors.map(element => {
					return {
						name: element.localLogoModule
							? element.localLogoModule.name
							: 'PrimaryBillboard',
						logoBase64: element.sponsorLogo
							? element.sponsorLogo.logoBase64
							: element.logoBase64
					};
				});

				commit('setLocalSponsors', sponsors);
			}
		},
		async getLifeStageSetupData({ commit, state }) {
			const stageSetup = await LifeStageService.getSetup(
				state.userData.studentId
			);
			commit('setStageSetupData', stageSetup);
		},
		async getProfileData({ commit, state, dispatch, getters }) {
			if (state.isLoadingProfileData === true) {
				return;
			}

			commit('setIsLoadingProfileData', true);

			const profileData = await ProfileService.getData(
				state.userData.studentId,
				getters.haveToUsePasscodes
			);

			commit('setProfileData', profileData);
			commit('setIsLoadingProfileData', false);

			if (!state.localSponsors) {
				dispatch('getLocalSponsors');
			}

			if (!state.avatarImg) {
				dispatch('getAvatar');
			}

			commit('setPauseState', profileData.simulation.isPaused);
		},
		async getPauseState({ commit, state }) {
			const profileData = await ProfileService.getPauseState(
				state.userData.studentId
			);

			commit('setPauseState', profileData);
		},
		async getAvatar({ commit, state }) {
			const avatar = await AvatarsService.getUserAvatar(
				state.userData.studentId
			);

			commit('setAvatarImg', avatar);
		},
		async getLifeStageData({ commit, state }) {
			const stageData = await LifeStageService.getData(
				state.userData.studentId,
				state.userData.studentLifeStageId
			);

			let minimumStudentPayment = 0;
			if (state.profileData) {
				minimumStudentPayment = state.profileData.minimumStudentLoanPayment;
			}

			stageData.outstandingDebt =
				minimumStudentPayment + stageData.minimumStudentCreditCardPayment;

			commit('setLifeStageData', stageData);

			commit(
				'setForcedToNextLifeStageState',
				stageData.isTransferToNextLifeStagePending
			);
		},
		async getExpenseCategoryGroups({ commit, state }) {
			const response = await ExpenseCategoryService.getCategoryGroups(
				state.userData,
				state.billboards
			);

			const {
				autoLoan,
				mortgageLoan,
				autoLoanBankingAndFinance
			} = await ExpenseCategoryService.getLoanCategories(
				state.userData.studentFacilityId
			);
			const hasAppliedForLoans = await ExpenseCategoryService.hasAppliedForLoan(
				state.userData.studentId,
				{
					autoLoan: autoLoan.expenseCategory_ID,
					mortgageLoan: mortgageLoan.expenseCategory_ID,
					autoLoanBankingAndFinance:
						autoLoanBankingAndFinance.expenseCategory_ID
				},
				state.userData.studentLifeStageId,
				true
			);

			commit('setAutoLoan', {
				...autoLoan,
				key: applicationLoanTypes.AUTO_LOAN,
				...hasAppliedForLoans.autoLoan
			});

			commit('setAutoLoanBanking', {
				...autoLoanBankingAndFinance,
				key: applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE,
				...hasAppliedForLoans.autoLoanBankingAndFinance
			});

			commit('setMortgageLoan', {
				...mortgageLoan,
				key: applicationLoanTypes.MORTGAGE_LOAN,
				...hasAppliedForLoans.mortgageLoan
			});

			if (!state.billboards) {
				commit('setBillboards', response.data.cmsExpenseCategoryGroups);
			}

			commit(
				'setExpenseCategoryGroups',
				response.data.cmsExpenseCategoryGroups
			);

			let categories = [];
			response.data.cmsExpenseCategoryGroups.forEach(categoryGroup => {
				categories = categories.concat(categoryGroup.cmsExpenseCategories);
			});

			const totalBudgetedCategories = categories.reduce((sum, item) => {
				const itemBudget = item.studentExpenseCategoryBudgets[0];

				if (itemBudget && itemBudget.budget && itemBudget.budget >= 0) {
					return sum + parseInt(itemBudget.budget);
				}
				return sum;
			}, 0);

			commit('setBudgetedCategoriesValue', totalBudgetedCategories);
		},
		logoutSingleSession: ({ commit }) => {
			const logoutURL = `${process.env.VUE_APP_API_URL}/api/Account/LogOut?all=false`;
			commit('setSessionLost', false);
			commit('logout', { logoutURL: logoutURL });
		},
		logout: ({ commit }) => {
			const logoutURL = `${process.env.VUE_APP_API_URL}/api/Account/LogOut`;
			commit('closeLogoutModal');
			commit('logout', { logoutURL: logoutURL });
		},
		sessionLost: ({ commit }) => {
			commit('setSessionLost', true);
		},
		async getInitialDebt({ commit, state }) {
			// If user is not logged, do nothing.
			// It is used in methods that are part of computed properties
			// and are triggered on logOut
			if (!state.userData) {
				return;
			}

			await graphqlClient
				.query({
					query: gql`
						{
							studentDebtBudgets(where: "studentId = ${state.userData.studentId} && lifeStage_ID = \\"${state.lifeStageData.lifeStage_ID}\\"" ){
								budget
								id
								lifeStage_ID
								debtType
								isDeleted
							}
							studentExpenseSubCategoryExpenditures(where: "studentId = ${state.userData.studentId} && lifeStage_ID = \\"${state.lifeStageData.lifeStage_ID}\\" && upperPaymentMethod_ID in (3, 5)") {
								id
								cmsExpenseSubCategory{
									name
									cost
									additionalCost
								}
								cmsPaymentMethod {
									upperPaymentMethod_ID
									name
									defaultName
								}
								amountSpent
							}
							mortgageLoan: studentExpenseSubCategoryExpenditures(where: "studentId = ${state.userData.studentId} && upperPaymentMethod_ID in (4) && loanIsPaid != true") {
								id
								cmsExpenseSubCategory{
									name
									cost
									additionalCost
								}
								cmsPaymentMethod {
									upperPaymentMethod_ID
									name
									defaultName
								}
								amountSpent
							}
						}
						`,
					fetchPolicy: 'no-cache'
				})
				.then(({ data }) => {
					let remapAsObject = {};

					data.studentDebtBudgets.forEach(item => {
						remapAsObject = {
							...remapAsObject,
							[item.debtType]: {
								...item
							}
						};
					});

					let expenditures = [];
					data.studentExpenseSubCategoryExpenditures.reduce(function(acc, x) {
						let current = acc.find(
							item =>
								item.cmsPaymentMethod.upperPaymentMethod_ID ==
								x.cmsPaymentMethod.upperPaymentMethod_ID
						);
						if (current) {
							current.cmsExpenseSubCategory.cost +=
								x.cmsExpenseSubCategory.cost;
							current.amountSpent += x.amountSpent;
						} else {
							acc.push(JSON.parse(JSON.stringify(x)));
						}
						return acc;
					}, expenditures);

					data.mortgageLoan.reduce(function(acc, x) {
						let current = acc.find(
							item =>
								item.cmsPaymentMethod.upperPaymentMethod_ID ==
								x.cmsPaymentMethod.upperPaymentMethod_ID
						);
						if (current) {
							current.cmsExpenseSubCategory.cost +=
								x.cmsExpenseSubCategory.cost;
							current.amountSpent += x.amountSpent;
						} else {
							acc.push(JSON.parse(JSON.stringify(x)));
						}
						return acc;
					}, expenditures);

					expenditures.forEach(item => {
						remapAsObject = {
							...remapAsObject,
							[item.cmsPaymentMethod.defaultName
								.replaceAll(' ', '')
								.replaceAll('-', '')]: {
								id: item.id,
								debtType: item.cmsPaymentMethod.defaultName
									.replaceAll(' ', '')
									.replaceAll('-', ''),
								cost: item.cmsExpenseSubCategory.cost,
								amount: item.amountSpent,
								additionalCost: item.cmsExpenseSubCategory.additionalCost,
								budget: 0
							}
						};
					});
					commit('setInitialDebt', remapAsObject);
				});
		},
		async updateLoansModalVisibility({ state, getters }) {
			if (getters.isAutoLoanDealershipVisible && getters.autoLoan.hasApplied) {
				await ExpenseCategoryService.updateLoansModalVisibility(state.autoLoan);
			}
			if (
				getters.isAutoLoanFinanceVisible &&
				getters.autoLoanBankingAndFinance.hasApplied
			) {
				await ExpenseCategoryService.updateLoansModalVisibility(
					state.autoLoanBankingAndFinance
				);
			}

			if (getters.isMortgageLoanVisible && getters.mortgageLoan.hasApplied) {
				await ExpenseCategoryService.updateLoansModalVisibility(
					state.mortgageLoan
				);
			}
		}
	}
});
